import React from 'react';
import { Lock, Check } from 'lucide-react';
import { useLLDPractice } from '../../context/LLDPracticeContext';
import {
  SECTION_SEQUENCE,
  SectionId,
  SectionStatus,
  getRecommendedSection,
} from '../../utils/sectionStateMachine';

/**
 * Section labels for the active LLD workspace sections. Testing is retained in
 * the type system for later re-enablement but is not part of the current flow.
 */
const SECTION_META: Record<SectionId, { label: string; hint: string; number: number }> = {
  problem: { label: 'Understand the Problem', hint: 'Read, clarify & scope', number: 1 },
  requirements: { label: 'Requirements', hint: 'Scope the problem', number: 2 },
  entities: { label: 'Entity Discovery', hint: 'Find the nouns', number: 3 },
  responsibilities: { label: 'Responsibilities', hint: 'Attributes & methods', number: 4 },
  relationships: { label: 'Relationships', hint: 'How they connect', number: 5 },
  uml: { label: 'UML Diagram', hint: 'Visualize the design', number: 6 },
  implementation: { label: 'Implementation', hint: 'Write the code', number: 7 },
  testing: { label: 'Testing', hint: 'Verify behavior', number: 8 },
};

const SectionNavigator: React.FC = () => {
  const { state, dispatch } = useLLDPractice();
  const { mode, currentSection, sectionStates, sectionProgress } = state.sessionState;

  const recommendedSection = mode === 'practice' ? getRecommendedSection(sectionProgress) : null;

  const completedCount = SECTION_SEQUENCE.filter((s) => sectionStates[s] === 'completed').length;
  const totalCount = SECTION_SEQUENCE.length;
  const pct = Math.round((completedCount / totalCount) * 100);

  const handleSectionClick = (section: SectionId) => {
    if (mode === 'learn' && sectionStates[section] === 'locked') return;
    dispatch({ type: 'SET_SECTION', payload: section });
  };

  const StatusNode: React.FC<{ section: SectionId; status: SectionStatus; isActive: boolean }> = ({ section, status, isActive }) => {
    if (status === 'completed') {
      return (
        <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0 ring-4 ring-white dark:ring-gray-900">
          <Check className="w-4 h-4 text-white" strokeWidth={3} />
        </div>
      );
    }
    if (status === 'locked') {
      return (
        <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0 ring-4 ring-white dark:ring-gray-900">
          <Lock className="w-3 h-3 text-gray-500 dark:text-gray-500" />
        </div>
      );
    }
    // active / available
    return (
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold ring-4 ring-white dark:ring-gray-900 transition-colors ${isActive
          ? 'bg-primary-600 text-white'
          : 'bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400'
          }`}
      >
        {SECTION_META[section].number}
      </div>
    );
  };

  return (
    <nav className="lld-section-navigator w-80 flex flex-col h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800">
      {/* Header: progress */}
      <div className="px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-500">Your progress</h2>
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 tabular-nums">{completedCount}/{totalCount}</span>
        </div>
        <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div className="h-full bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Stepper */}
      <ul className="relative flex-1 overflow-y-auto px-5 py-4">
        {/* vertical connector line */}
        <span className="absolute left-[34px] top-7 bottom-7 w-px bg-gray-200 dark:bg-gray-800" aria-hidden="true" />

        {SECTION_SEQUENCE.map((section) => {
          const status = sectionStates[section];
          const meta = SECTION_META[section];
          const isActive = section === currentSection;
          const isLocked = status === 'locked';
          const isRecommended = section === recommendedSection;
          const isClickable = !(mode === 'learn' && isLocked);

          return (
            <li key={section} className="relative">
              <button
                onClick={() => handleSectionClick(section)}
                disabled={!isClickable}
                aria-current={isActive ? 'step' : undefined}
                className={`group w-full flex items-start gap-3 py-2.5 text-left transition-colors ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <StatusNode section={section} status={status} isActive={isActive} />

                <div className={`flex-1 min-w-0 rounded-lg px-2.5 py-1 -mt-0.5 transition-colors ${isActive ? 'bg-primary-50 dark:bg-primary-900/20' : 'group-hover:bg-gray-50 dark:group-hover:bg-gray-800/60'}`}>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium leading-snug ${isActive
                      ? 'text-primary-700 dark:text-primary-300'
                      : isLocked
                        ? 'text-gray-400 dark:text-gray-600'
                        : 'text-gray-700 dark:text-gray-300'
                      }`}>
                      {meta.label}
                    </span>
                    {isRecommended && mode === 'practice' && !isActive && (
                      <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide rounded bg-primary-100 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 flex-shrink-0">
                        Next
                      </span>
                    )}
                  </div>
                  <div className={`text-[11px] truncate ${isActive ? 'text-primary-500/80 dark:text-primary-400/70' : 'text-gray-500 dark:text-gray-500'}`}>
                    {meta.hint}
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default SectionNavigator;
